import { Controller } from "stimulus"
import SlimSelect from "slim-select"

// Turns the opportunity's event field into a type-ahead SlimSelect and
// narrows the available events to those whose category matches one of the
// categories currently checked on the opportunity. Events with no category
// are always offered (they are not tied to any category), and the event
// already saved on the opportunity is never filtered out so editing can
// never silently drop it.
//
// The full option list is read from the data-event-picker-events JSON
// attribute rather than the select's <option> elements: SlimSelect v1's
// setData() rebuilds the native options (dropping filtered-out entries and
// their data attributes), so the DOM is not a reliable source after init —
// especially on Turbo cache restores.
export default class extends Controller {
  static targets = ["category", "select"]

  connect() {
    // The event select is only rendered when there are events to offer, but
    // the controller is attached to the form unconditionally.
    if (!this.hasSelectTarget) return

    this.options = JSON.parse(this.data.get("events") || "[]").map((event) => ({
      value: String(event.id),
      text: event.label,
      categoryId: event.category_id
    }))

    // A Turbo cache snapshot may already contain SlimSelect markup from a
    // previous visit (the snapshot is taken before disconnect runs). Remove
    // any stray widgets so re-initializing doesn't render the select twice.
    this.removeStrayWidgets()

    this.slimSelect = new SlimSelect({
      select: this.selectTarget,
      addToBody: true
    })

    this.applyFilter()

    // Tear down before Turbo snapshots the page so the cached DOM holds the
    // pristine native select instead of the generated SlimSelect widget.
    this.beforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.teardown()
  }

  teardown() {
    if (this.slimSelect) {
      try {
        this.slimSelect.destroy()
      } catch (error) {
        // SlimSelect DOM may already be gone on Turbo teardown.
      }
      this.slimSelect = null
    }
    this.removeStrayWidgets()
  }

  removeStrayWidgets() {
    if (!this.hasSelectTarget) return

    const parent = this.selectTarget.parentNode
    if (!parent) return

    parent.querySelectorAll(".ss-main").forEach((widget) => widget.remove())
    this.selectTarget.style.display = ""
  }

  filter() {
    this.applyFilter()
  }

  applyFilter() {
    if (!this.slimSelect) return

    const selectedCategoryIds = this.categoryTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => parseInt(checkbox.value, 10))
      .filter((id) => !Number.isNaN(id))

    const currentValue = this.slimSelect.selected()

    const data = [{ value: "", text: this.blankText() }].concat(
      this.options
        .filter((option) => this.optionVisible(option, selectedCategoryIds, currentValue))
        .map((option) => ({
          value: option.value,
          text: option.text,
          selected: option.value === currentValue
        }))
    )

    this.slimSelect.setData(data)
  }

  blankText() {
    const blank = Array.from(this.selectTarget.options).find((option) => option.value === "")
    return blank ? blank.text : "-- No event --"
  }

  optionVisible(option, selectedCategoryIds, currentValue) {
    if (option.value === currentValue) return true
    if (selectedCategoryIds.length === 0) return true
    if (option.categoryId === null) return true
    return selectedCategoryIds.includes(option.categoryId)
  }
}
